import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { INHERITANCE_FAQ_CATEGORIES } from "@/content/inheritance-corpus/faq-defs";

export const Route = createFileRoute("/en/inheritance/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/inheritance-corpus");
    return { faqs: m.INHERITANCE_FAQ_EN };
  },
  component: InheritanceFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Thai Wills and Estate Administration — FAQ (${faqs.length})`,
        description:
          "Expat FAQ on the five valid forms of Thai will, whether a foreign will covers Thai assets, the six classes of statutory heirs, court-appointed estate administrators, foreigners inheriting land and condominiums, bank and share transfers, inheritance tax and cross-border documents.",
        path: "/en/inheritance/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/inheritance/faq" }],
        keywords: [
          "thai will foreigner",
          "estate administrator thailand court",
          "statutory heirs thailand",
          "foreigner inherit land thailand",
          "thailand inheritance tax",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Wills & Inheritance FAQ", url: `${site}/en/inheritance/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/inheritance/faq")) },
      ],
    };
  },
});

function InheritanceFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Wills &amp; Inheritance FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Thai Wills, Heirs and Estate Administration — {faqs.length} FAQs
        </h1>
        <p className="mb-6 text-muted-foreground">
          Answers follow Book VI (Succession) of the Civil and Commercial Code, the Inheritance Tax Act B.E. 2558,
          the Land Code and Condominium Act, Land Department practice (dol.go.th), Revenue Department rules
          (rd.go.th), the Office of the Judiciary (coj.go.th) and the Office of the Council of State
          (krisdika.go.th). General information only, not case-specific legal advice, and no court outcome is guaranteed.
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/inheritance/faq" className="text-primary underline">FAQ พินัยกรรมและมรดก →</a>
        </div>
        <section aria-label="Inheritance FAQ topics" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {INHERITANCE_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/inheritance/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">Related services</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/en/property/faq">Property ownership &amp; conveyancing</a></li>
            <li><a className="text-primary underline" href="/en/notary/faq">Notarial services and powers of attorney</a></li>
            <li><a className="text-primary underline" href="/en/translation/faq">Certified translation</a></li>
            <li><a className="text-primary underline" href="/en/consular-legalization/faq">MFA legalisation and Apostille</a></li>
          </ul>
        </section>
        <section className="mt-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">Still need an answer?</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Ask our team about document lists, court filing steps and timing by phone, LINE or email — Mon-Sat 9:00-18:00.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">Contact the team →</a>
        </section>
        <FaqSourceNote topic="inheritance" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}